import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useProducerById } from '@/hooks/useMyProducers';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * 3-screen welcome flow for newly onboarded producers.
 * Shown once per member after accepting an invitation.
 * Editorial, calm, no checklists.
 */
export default function ProducerWelcomeFlowPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);

  const dashboardPath = `/app/producer/farm/${farmId}`;

  // Check if already onboarded
  const { data: membership, isLoading: memberLoading } = useQuery({
    queryKey: ['producer-member-onboarding', farmId, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('producer_members')
        .select('onboarded_at')
        .eq('producer_id', farmId!)
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!farmId && !!user?.id,
  });

  // Already onboarded → skip to dashboard
  useEffect(() => {
    if (!memberLoading && membership?.onboarded_at) {
      navigate(dashboardPath, { replace: true });
    }
  }, [memberLoading, membership?.onboarded_at, dashboardPath, navigate]);

  // Producer data for preview step
  const { producer, isLoading: producerLoading } = useProducerById(farmId);

  // Cover image from media_assets
  const { data: coverAsset } = useQuery({
    queryKey: ['producer-cover-asset', farmId],
    queryFn: async () => {
      const { data } = await supabase
        .from('media_assets')
        .select('path_640, path_320, bucket')
        .eq('owner_id', farmId!)
        .eq('owner_type', 'producer')
        .eq('kind', 'cover')
        .order('sort_order', { ascending: true })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!farmId,
  });

  // Mark onboarded
  const completeOnboarding = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('producer_members')
        .update({ onboarded_at: new Date().toISOString() })
        .eq('producer_id', farmId!)
        .eq('user_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['producer-member-onboarding'] });
      navigate(dashboardPath, { replace: true });
    },
  });

  if (!farmId) {
    navigate('/app/producer', { replace: true });
    return null;
  }

  if (memberLoading || producerLoading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="w-full max-w-md space-y-4 px-4">
          <Skeleton className="h-8 w-3/4 mx-auto" />
          <Skeleton className="h-4 w-1/2 mx-auto" />
        </div>
      </div>
    );
  }

  if (!producer) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 text-center">
        <p className="text-muted-foreground mb-6">Vi hittar inte gården just nu.</p>
        <Button variant="outline" onClick={() => navigate('/app/producer')}>
          Tillbaka
        </Button>
      </div>
    );
  }

  // Resolve cover image URL
  let coverUrl: string | null = null;
  if (coverAsset) {
    const path = coverAsset.path_640 || coverAsset.path_320;
    if (path && coverAsset.bucket === 'public-media') {
      const { data: urlData } = supabase.storage.from('public-media').getPublicUrl(path);
      coverUrl = urlData?.publicUrl || null;
    }
  }
  // No legacy fallback – media_assets is source of truth

  const steps = [
    // Step 0: Tone
    <WelcomeStepTone key="tone" onNext={() => setStep(1)} />,
    // Step 1: Preview
    <WelcomeStepPreview
      key="preview"
      name={producer.name}
      region={producer.county || producer.city || null}
      coverUrl={coverUrl}
      shortDescription={producer.short_description}
      onNext={() => setStep(2)}
    />,
    // Step 2: Tempo
    <WelcomeStepTempo
      key="tempo"
      onFinish={() => completeOnboarding.mutate()}
      isSubmitting={completeOnboarding.isPending}
    />,
  ];

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4">
      {/* Discrete step indicator */}
      <div className="flex gap-2 mb-12">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`h-1 w-8 rounded-full transition-colors ${
              i <= step ? 'bg-foreground/30' : 'bg-foreground/8'
            }`}
          />
        ))}
      </div>

      {steps[step]}
    </div>
  );
}

/* ─── Step components ─── */

function WelcomeStepTone({ onNext }: { onNext: () => void }) {
  return (
    <div className="max-w-2xl text-center space-y-8">
      <h1 className="text-2xl sm:text-3xl font-medium tracking-wide">
        Välkommen till Farmly.
      </h1>
      <div className="text-base sm:text-lg leading-[1.8] text-muted-foreground max-w-lg mx-auto space-y-4">
        <p>Farmly är inte en marknadsplats i första hand.</p>
        <p>Det är ett sätt att bli upptäckt i rätt sammanhang.</p>
        <p>Vi visar inte alla, hela tiden.</p>
        <p>Synlighet är inte ett löfte.</p>
        <p>Det vi försöker skapa är rätt gäster, i rätt takt.</p>
      </div>
      <Button
        variant="ghost"
        className="mt-4 text-sm"
        onClick={onNext}
      >
        Jag förstår →
      </Button>
    </div>
  );
}

function WelcomeStepPreview({
  name,
  region,
  coverUrl,
  shortDescription,
  onNext,
}: {
  name: string;
  region: string | null;
  coverUrl: string | null;
  shortDescription: string | null;
  onNext: () => void;
}) {
  return (
    <div className="max-w-2xl w-full text-center space-y-8">
      <h1 className="text-2xl sm:text-3xl font-medium tracking-wide">
        Så här kommer ni att synas.
      </h1>

      {/* Minimal producer preview card */}
      <div className="rounded-xl overflow-hidden border border-border/40 max-w-sm mx-auto text-left">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={name}
            className="w-full h-44 object-cover"
          />
        ) : (
          <div className="w-full h-44 bg-muted/40" />
        )}
        <div className="p-5 space-y-1.5">
          <p className="font-medium text-base">{name}</p>
          {region && (
            <p className="text-xs text-muted-foreground">{region}</p>
          )}
          {shortDescription ? (
            <p className="text-sm text-muted-foreground leading-relaxed mt-2 line-clamp-2">
              {shortDescription}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground/50 italic mt-2">
              Ingen beskrivning ännu
            </p>
          )}
        </div>
      </div>

      <div className="text-sm text-muted-foreground space-y-1">
        <p>En bild räcker.</p>
        <p>En mening räcker.</p>
      </div>

      <Button variant="ghost" className="text-sm" onClick={onNext}>
        Fortsätt
      </Button>
    </div>
  );
}

function WelcomeStepTempo({
  onFinish,
  isSubmitting,
}: {
  onFinish: () => void;
  isSubmitting: boolean;
}) {
  return (
    <div className="max-w-2xl text-center space-y-8">
      <h1 className="text-2xl sm:text-3xl font-medium tracking-wide">
        Ni styr tempot.
      </h1>
      <div className="text-base sm:text-lg leading-[1.8] text-muted-foreground max-w-lg mx-auto space-y-4">
        <p>Ni kan vara synliga.</p>
        <p>Ni kan pausa.</p>
        <p>Inget raderas.</p>
      </div>
      <Button
        onClick={onFinish}
        disabled={isSubmitting}
        className="mt-4"
      >
        {isSubmitting ? 'Laddar…' : 'Gå till Farmly'}
      </Button>
    </div>
  );
}
